import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useGeolocation } from '../../hooks/useGeolocation';
import ProductCard from './ProductCard';

/**
 * Componente que muestra productos recomendados según el clima
 * Con geolocalización automática del navegador
 */
const WeatherProducts = () => {
  const navigate = useNavigate();
  const { location, loading: geoLoading } = useGeolocation();
  const [weatherData, setWeatherData] = useState(null);
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!geoLoading) {
      fetchWeatherAndProducts();
    }
  }, [location, geoLoading]);

  const fetchWeatherAndProducts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      
      // Construir URL con coordenadas si están disponibles
      let weatherUrl = `${apiUrl}/weather/recommendations`;
      if (location) {
        weatherUrl += `?lat=${location.latitude}&lon=${location.longitude}`;
      }
      
      const weatherResponse = await axios.get(weatherUrl);
      
      if (weatherResponse.data.success) {
        setWeatherData(weatherResponse.data.weather);
        setRecommendations(weatherResponse.data.recommendations);
        
        // Buscar productos usando las keywords del clima
        const keywords = weatherResponse.data.recommendations.keywords;
        const productsResponse = await axios.get(`${apiUrl}/products`, {
          params: { limit: 50 } // Obtener más productos para filtrar mejor
        });
        
        if (productsResponse.data) {
          // Manejar diferentes formatos de respuesta
          const allProducts = Array.isArray(productsResponse.data) 
            ? productsResponse.data 
            : (productsResponse.data.products || []);
          
          // Validar que sea un array
          if (!Array.isArray(allProducts) || allProducts.length === 0) {
            console.warn('No hay productos disponibles');
            setLoading(false);
            return;
          }
          
          // Filtrar productos según keywords del clima
          let filteredProducts = allProducts.filter(product => {
            const productName = product.name?.toLowerCase() || '';
            const productDesc = product.description?.toLowerCase() || '';
            const productTags = product.tags?.map(t => t.toLowerCase()) || [];
            
            return keywords.some(keyword => 
              productName.includes(keyword) || 
              productDesc.includes(keyword) ||
              productTags.some(tag => tag.includes(keyword))
            );
          });
          
          // Si no hay productos filtrados, mostrar los primeros 8
          if (filteredProducts.length === 0) {
            filteredProducts = allProducts.slice(0, 8);
          }
          
          setProducts(filteredProducts.slice(0, 8));
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error obteniendo productos del clima:', error);
      // No mostrar la sección si hay error
      setLoading(false);
    }
  };

  const getWeatherEmoji = (main) => {
    const emojiMap = {
      'Clear': '☀️',
      'Clouds': '☁️',
      'Rain': '🌧️',
      'Drizzle': '🌦️',
      'Thunderstorm': '⛈️',
      'Snow': '❄️',
      'Mist': '🌫️',
      'Fog': '🌫️',
    };
    return emojiMap[main] || '🌤️';
  };

  const handleViewAll = () => {
    if (recommendations?.categorySlug) {
      navigate(`/tienda?category=${recommendations.categorySlug}`);
    } else {
      navigate('/tienda');
    }
  };

  if (loading || geoLoading) {
    return (
      <div className="weather-products-section">
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando recomendaciones...</span>
            </div>
            <p className="mt-3 text-muted">Obteniendo clima de tu ubicación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!weatherData || products.length === 0) {
    return null;
  }

  return (
    <div className="weather-products-section">
      <div className="weather-container">
        {/* Header con info del clima */}
        <div className="weather-header">
          <div className="weather-info-compact">
            <span className="weather-emoji">{getWeatherEmoji(weatherData.main)}</span>
            <div className="weather-text">
              <h3 className="weather-title">
                Perfecto para hoy
              </h3>
              <p className="weather-subtitle">
                {weatherData.temperature}°C en {weatherData.city} • {weatherData.description}
              </p>
            </div>
          </div>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={handleViewAll}
          >
            Ver todo
          </button>
        </div>

        {/* Mensaje de recomendación */}
        {recommendations && (
          <div className="weather-message">
            {recommendations.message}
          </div>
        )}

        {/* Grid de productos */}
        <div className="weather-products-grid">
          {products.map((product, index) => (
            <ProductCard 
              key={product._id || index} 
              product={product} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeatherProducts;
